/**
 * @file useTokenRefresh.js
 * @feature Auth
 * @description Encapsulates all token-refresh logic: silent acquire,
 * expiry calculation, and scheduled timer. Returns a stable `refreshToken`
 * function and a `getTimeUntilRefresh` helper.
 *
 * FDD rationale: token refresh is one cohesive behaviour; isolating it
 * means Auth and EULA features never need to know how tokens work.
 */

import { useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  AUTH_SCOPES,
  TOKEN_REFRESH_BUFFER_MS,
  TOKEN_NEAR_EXPIRY_MS,
} from '../../config/Auth.config';

/**
 * @param {Function} onTokenRefreshed  – callback(idToken) after a successful refresh
 */
export const useTokenRefresh = (onTokenRefreshed) => {
  const { instance } = useMsal();
  // const navigate = useNavigate();

  /** Silently acquires a new token for the given account. */
  const refreshToken = useCallback(
    async (account) => {
      if (!account) return false;
      try {
        const response = await instance.acquireTokenSilent({
          scopes: AUTH_SCOPES,
          account,
          forceRefresh: true,
        });

        if (response.idToken) {
          sessionStorage.setItem('id_token', response.idToken);
          await onTokenRefreshed(response.idToken);
          return true;
        }
        return false;
      } catch (error) {
        if (error.name === 'InteractionRequiredAuthError') {
          instance.loginRedirect();
        }
        return false;
      }
    },
    [instance, onTokenRefreshed],
  );

  /** Returns ms until the token should be proactively refreshed. */
  const getTimeUntilRefresh = useCallback(() => {
    const account = instance.getActiveAccount();
    if (!account?.idTokenClaims?.exp) return TOKEN_REFRESH_BUFFER_MS;

    const expiresAt = account.idTokenClaims.exp * 1000;
    return Math.max(0, expiresAt - Date.now() - TOKEN_REFRESH_BUFFER_MS);
  }, [instance]);

  /** True when the active token is within the "near expiry" window. */
  const isNearExpiry = useCallback(() => {
    const account = instance.getActiveAccount();
    if (!account?.idTokenClaims?.exp) return true;
    return account.idTokenClaims.exp * 1000 - Date.now() < TOKEN_NEAR_EXPIRY_MS;
  }, [instance]);

  return { refreshToken, getTimeUntilRefresh, isNearExpiry };
};